import axios from 'axios';
import axiosRetry from 'axios-retry';
import { camelizeKeys, decamelizeKeys } from 'humps';
import cookie from 'js-cookie';
import isInteger from 'lodash/isInteger';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import has from 'lodash/has';

export function checkStatus (response) {
  if (
    response &&
    response.data &&
    has(response.data, 'ok') &&
    !response.data.ok
  ) {
    return { data: response.data, error: true };
  }

  if (
    (response.status >= 200 && response.status < 300) ||
    (response.data && !response.data.reason)
  ) {
    if (
      response.data &&
      !isUndefined(response.data.count) &&
      isInteger(response.data.count)
    ) {
      return { data: camelizeKeys(response.data), error: false };
    } else if (response.data && response.data.results) {
      return { data: camelizeKeys(response.data.results), error: false };
    } else if (response.data) {
      return { data: camelizeKeys(response.data), error: false };
    }
  }

  return { data: response.data, error: true };
}

export function catchError (error) {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return {
      error: error.response.data || 'No response data',
      status: error.response.status
    };
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    return { error: error.message || 'Something bad happened' };
  }

  // Something happened in setting up the request that triggered an Error
  return { error: error.message || 'Something bad happened' };
}

let headers = {
  'Content-Type': 'application/json',
  'x-modsy-client-type': 'wc',
  'x-modsy-client-version': process.env.REACT_APP_VERSION || 'local'
};

if (cookie.get('jwtToken')) {
  headers = Object.assign({}, headers, {
    Authorization: `JWT ${cookie.get('jwtToken')}`
  });
}

export const api = axios.create({
  withCredentials: false
});

axiosRetry(api, { retries: 3 });

api.interceptors.request.use(
  config => {
    if (cookie.get('jwtToken')) {
      config.headers.Authorization = `JWT ${cookie.get('jwtToken')}`;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export function get (url, options, raw = false, addlHeaders) {
  const requestHeaders = addlHeaders ? { ...headers, ...addlHeaders } : headers;
  const config = { headers: requestHeaders, ...options };
  return api
    .get(url, config)
    .then(response => raw ? response : checkStatus(response))
    .catch(catchError);
}

export function post (url, data, raw = false,
  onUploadProgressCallback = () => {}, addlHeaders = null) {
  const requestHeaders = addlHeaders ? { ...headers, ...addlHeaders } : headers;
  const config = {
    headers: requestHeaders
    // Mobile performance bug with proper upload process tracking.
    // onUploadProgress: onUploadProgressCallback
  };
  return api
    .post(url, data, config)
    .then(response => raw ? response : checkStatus(response))
    .catch(catchError);
}

export function patch (url, data, raw = false) {
  const removeEmptyValues = omitBy(data, i => !i && typeof i !== 'boolean'); // WHAT IF YOU HAVE A VALUE THAT IS FALSE
  const config = { headers };
  return api
    .patch(url, raw ? data : decamelizeKeys(removeEmptyValues), config)
    .then(response => raw ? response : checkStatus(response))
    .catch(catchError);
}

export function patchAllowEmpty (url, data, raw = false) {
  const config = { headers };
  return api
    .patch(url, raw ? data : decamelizeKeys(data), config)
    .then(response => raw ? response : checkStatus(response))
    .catch(catchError);
}

export function put (url, data, raw = false) {
  const removeEmptyValues = omitBy(data, i => !i && typeof i !== 'boolean'); // WHAT IF YOU HAVE A VALUE THAT IS FALSE
  const config = { headers };
  return api
    .put(url, raw ? data : decamelizeKeys(removeEmptyValues), config)
    .then(checkStatus)
    .catch(catchError);
}

export function _delete (url, data = {}, raw = false) {
  return api({
    method: 'delete',
    url,
    data,
    headers
  }).then(response => raw ? response : checkStatus(response))
    .catch(catchError);
}
